import React from 'react';
import { Form, type InputComponentProps } from '../../../design-system';
import type { Address as IAddress } from '../../../utils/api';
import { getNestedHelperText, useFormikContext } from '../../../utils/formUtils';
import { addressValidationSchema, getAddressAsString } from '../../../utils/locations/address';
import type { LocationFields } from '../../../utils/locations/FieldDefinitions';
import { Dialog } from '../../Dialog';
import { TextField } from '../../TextField';
import { CountryInput, CountryValue } from '../Country';
import { StateInput, StateValue } from '../State';

interface SearchFields {
  search: string;
}

const initialValues: IAddress & SearchFields = {
  search: '',
  address1: '',
  address2: '',
  address3: '',
  city: '',
  postCode: '',
  state: '',
  country: '',
};

export const AddressDialogInput: React.FC<Pick<InputComponentProps<IAddress>, 'disabled' | 'helperText' | 'name'>> = ({
  disabled,
  helperText,
  name,
}) => {
  const {
    errors: { address: error },
    handleBlur,
    setFieldValue,
    values: { address: value },
  } = useFormikContext<LocationFields>();

  const [isOpen, setIsOpen] = React.useState(false);

  const onSubmit = React.useCallback(
    (values: IAddress & SearchFields) => {
      const { search, ...address } = values;
      setFieldValue({ key: 'address', value: address });
      setIsOpen(false);
    },
    [setFieldValue],
  );

  const rows = Object.keys(value).filter((val) => !!value[val as keyof IAddress]).length;

  return (
    <>
      <TextField
        InputProps={{
          readOnly: true,
          style: { cursor: 'pointer' },
          inputProps: { style: { cursor: 'pointer', lineHeight: '19px', userSelect: 'none' } },
        }}
        disabled={disabled}
        error={!!error}
        fullWidth
        helperText={getNestedHelperText(helperText)}
        minRows={rows > 1 ? `${rows}` : '1'}
        multiline
        name={name}
        onBlur={handleBlur({ key: 'address' })}
        onClick={disabled ? undefined : () => setIsOpen(true)}
        placeholder="Enter address manually..."
        value={getAddressAsString(value).split(', ').join('\n')}
      />

      {/* Edit address dialog */}
      <Dialog isOpen={isOpen} onClose={() => setIsOpen(false)} title="Edit address">
        <Form<IAddress & SearchFields>
          fields={[
            { id: 'address1', label: 'Address line 1' },
            { id: 'address2', label: 'Address line 2' },
            { id: 'address3', label: 'Address line 3' },
            { id: 'city', label: 'City' },
            { id: 'postCode', label: 'Post code' },
            {
              id: 'state',
              label: 'State',
              inputComponent: (props: InputComponentProps<string>) => (
                <StateInput<IAddress>
                  disabled={props.disabled}
                  error={props.error}
                  helperText={props.helperText}
                  name={props.name}
                  onBlur={props.onBlur}
                  onChange={props.onChange}
                  value={props.value}
                />
              ),
              valueComponent: StateValue,
            },
            {
              id: 'country',
              label: 'Country',
              inputComponent: (countryInputProps: InputComponentProps<string>) => (
                <CountryInput
                  disabled={countryInputProps.disabled}
                  error={countryInputProps.error}
                  helperText={countryInputProps.helperText}
                  name={countryInputProps.name}
                  onBlur={countryInputProps.onBlur}
                  onChange={countryInputProps.onChange}
                  value={countryInputProps.value}
                />
              ),
              valueComponent: CountryValue,
            },
          ]}
          initialValues={{ ...initialValues, ...value }}
          validationSchema={addressValidationSchema}
          onSubmit={onSubmit}
          isEditing
          labels={{ save: 'Change' }}
          formProps={{ autoComplete: 'off' }}
        />
      </Dialog>
    </>
  );
};
